import type { ReactNode } from "react";
import { t } from "@/i18n-js/instance";
import type { Paywall, PaywallCurrency } from "@/react/types";
import { formatDisplayAmountString } from "@circle-react/components/Paywalls/Admin/utils";
import { copyPaywallCheckoutUrlWithPrefix } from "@circle-react/helpers/paywallCheckoutHelpers";
import { Icon } from "@circle-react-shared/Icon";
import { Badge } from "@circle-react-uikit/Badge";
import { IconDropdownMenu } from "@circle-react-uikit/IconDropdownMenu";
import { usePaywallsAdminFormModal } from "../FormModal/";

interface UseListProps {
  paywalls?: Paywall[];
  refetchPaywalls: () => void;
}

interface DataSourceItem {
  [key: string]: unknown;
  action: ReactNode;
  actionMenu: ReactNode;
  displayName: string;
  key: string;
  preferred_price: string;
  status: ReactNode;
}

interface UseListReturn {
  dataSource: DataSourceItem[];
  showPaywallAdminFormModalToCreate: () => void;
}

export const useList = ({
  paywalls,
  refetchPaywalls,
}: UseListProps): UseListReturn => {
  const { show } = usePaywallsAdminFormModal();

  const showPaywallAdminFormModalToEdit = (paywall: Paywall) => {
    void show({ paywall, refetchPaywalls });
  };
  const showPaywallAdminFormModalToCreate = () => {
    void show({ paywall: undefined, refetchPaywalls });
  };

  const dataSource = paywalls?.map((paywall): DataSourceItem => {
    const statusStates = {
      active: {
        label: t("settings.paywalls.status.active"),
        variant: "success" as const,
      },
      inactive: {
        label: t("settings.paywalls.status.inactive"),
        variant: "default" as const,
      },
      draft: {
        label: t("settings.paywalls.status.draft"),
        variant: "default" as const,
      },
      notIdentified: {
        label: "",
        variant: "default" as const,
      },
    };
    const selectedStatusObject =
      statusStates[paywall.status as keyof typeof statusStates] ??
      statusStates["notIdentified"];

    const preferredPrice =
      paywall.stripe_prices_attributes.find(
        price_attribute => price_attribute.preferred,
      ) ?? paywall.stripe_prices_attributes[0];

    const preferredPriceText = preferredPrice
      ? formatDisplayAmountString(
          paywall.currency as PaywallCurrency,
          preferredPrice.interval || null,
          preferredPrice.interval_count || 1,
          preferredPrice.amount,
        )
      : t("settings.paywalls.list.form.no_preferred_price_selected");

    const menuItems = [
      {
        key: "edit-paywall",
        label: t("settings.paywalls.list.form.edit"),
        onClick: () => showPaywallAdminFormModalToEdit(paywall),
      },
    ];

    return {
      key: paywall.id.toString(),
      displayName: paywall.name || paywall.display_name,
      preferred_price: preferredPriceText,
      status: (
        <Badge variant={selectedStatusObject.variant}>
          {selectedStatusObject.label}
        </Badge>
      ),
      action: (
        <button
          type="button"
          className="text-[12px]"
          onClick={() => {
            void copyPaywallCheckoutUrlWithPrefix(
              paywall.checkout_path || "",
              "#paywalls-list",
            );
          }}
        >
          <Icon type="20-copy" className="mr-2" />
          <span>{t("settings.paywalls.list.form.copy_url")}</span>
        </button>
      ),
      actionMenu: (
        <IconDropdownMenu
          menuItems={menuItems}
          dropdownSide="left"
          shouldCloseOnClickItem
        />
      ),
    };
  });

  return {
    showPaywallAdminFormModalToCreate,
    dataSource: dataSource || [],
  };
};
